import type {
  LiveGalleryPublic,
  LiveGalleryCreate,
  LiveGalleryUpdate,
} from "../types/Gallery";
import { apiGet, apiPost, apiPatch, apiDelete, API_URL, API_PATH } from "./apiClient";

export const fetchGalleries = (
  projectId: string,
): Promise<LiveGalleryPublic[]> => {
  return apiGet<LiveGalleryPublic[]>(
    `/projects/${projectId}/galleries`,
  );
};

export const createGallery = (
  projectId: string,
  data: LiveGalleryCreate,
): Promise<LiveGalleryPublic> => {
  return apiPost<LiveGalleryPublic>(
    `/projects/${projectId}/galleries`,
    data,
  );
};

export const updateGallery = (
  galleryId: string,
  data: LiveGalleryUpdate,
): Promise<LiveGalleryPublic> => {
  return apiPatch<LiveGalleryPublic>(
    `/galleries/${galleryId}`,
    data,
  );
};

export const deleteGallery = (
  galleryId: string,
): Promise<void> => {
  return apiDelete<void>(
    `/galleries/${galleryId}`,
  );
};

// --- Public (no auth) gallery endpoints ---

/**
 * Build the full public API base URL for gallery endpoints.
 * Used by components that need raw fetch/EventSource (not apiClient).
 */
export const getGalleryPublicBaseUrl = () => `${API_URL}${API_PATH}`;

export const fetchPublicGallery = async (slug: string): Promise<Response> => {
  return fetch(`${API_URL}${API_PATH}/live/${slug}`, {
    credentials: "include",
  });
};

export const authenticateGallery = async (
  slug: string,
  password: string,
): Promise<Response> => {
  return fetch(`${API_URL}${API_PATH}/live/${slug}/auth`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ password }),
    credentials: "include",
  });
};

export const getGalleryStreamUrl = (slug: string, lastEventId?: string | null): string => {
  const url = new URL(`${API_URL}${API_PATH}/live/${slug}/stream`);
  if (lastEventId) {
    url.searchParams.set("lastEventId", lastEventId);
  }
  return url.toString();
};

// --- Download endpoints (Story 3.3) ---

export const getVideoDownloadUrl = (slug: string, videoId: string): string =>
  `${API_URL}${API_PATH}/live/${slug}/videos/${videoId}/download`;

export const downloadZip = async (
  slug: string,
  videoIds: string[],
): Promise<Response> => {
  return fetch(`${API_URL}${API_PATH}/live/${slug}/download-zip`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ video_ids: videoIds }),
    credentials: "include",
  });
};
